import { filter, forEach, get, isEmpty, last, pick, uniq } from "lodash"
import moment from "moment"
import { applyMiddleware, createStore } from "redux"
import createSagaMiddleware from "redux-saga"
import { testSaga } from "redux-saga-test-plan"
import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { INTRADAY_LAST_BATCH_RESOURCE, generateIntradayBatchUrlAt, intradayLastBatchResource } from "helpers/intraday"
import { Creators as DataCreators, reducer as dataReducer, saga as dataSaga } from "../data"
import { Creators as GraphqlCreators } from "../graphql"
import { INITIAL_STATE, CANCELLED, SOCKET_TEARDOWN_GRACE_MS, Types, getSocket, handleCleanupData, handleQueryExecuted, handleRegisterQuery, handleSetRefreshStatus, REFRESHING, handleUnregisterQuery, handleRefreshQuery, handleCancelQuery, handleQueueRefresh, handleClearQueuedRefresh, handleRegisterQuerySaga, Creators, sagaSelectors, closeAllSockets, openSocket, handleUnregisterQuerySaga, handleUnregisterModuleQueries, handleRefreshData, handleRefreshQuerySaga, handleAllQueriesExecuted, reducer as intradayReducer, saga } from "../intraday"
import { select } from "redux-saga/effects"
import { sendQuery } from "api/graphql"
import { get as apiGet, post as apiPost } from "api"

jest.mock("api")

jest.mock("socket.io-client", () => ({
  __esModule: true,
  default: () => mockCreateSocket(),
}))

const mockSockets = []

function mockCreateSocket() {
  const listeners = {}
  const socket = {
    connected: false,
    disconnectCount: 0,
    on: (event, handler) => {
      listeners[event] = [...(listeners[event] || []), handler]
    },
    off: (event, handler) => {
      listeners[event] = handler ? (listeners[event] || []).filter(existing => existing !== handler) : []
    },
    disconnect: () => {
      socket.connected = false
      socket.disconnectCount += 1
    },
    emitToClient: (event) => {
      if (event === "connect") {
        socket.connected = true
      }
      forEach([...(listeners[event] || [])], handler => handler())
    },
  }
  mockSockets.push(socket)
  return socket
}

function mockEmitSocketEvent(event) {
  forEach([...mockSockets], socket => socket.emitToClient(event))
}

function mockResetSocketListeners() {
  mockSockets.length = 0
}

const defaultVars = {
  channel: "account_resource",
  resource: "resource",
  module: "module",
  currentDataIdx: 0,
  refreshIdx: 0,
  selectors: [
    select(sagaSelectors.getRefresh),
    select(sagaSelectors.getQueries),
  ],
}

const graphQlAction = {
  channel: defaultVars.channel,
  resource: defaultVars.resource,
  queryInfo: {
    id: "graphqlQuery",
    query: "query { }",
    variables: {},
    account: "account",
    options: null,
    beta: false,
    isGraphQl: true,
  },
}

const apiAction = {
  channel: defaultVars.channel,
  resource: defaultVars.resource,
  queryInfo: {
    id: "apiQuery",
    url: "http://localhost:3000",
    payload: { param: "value" },
    noToken: false,
    isGraphQl: false,
  },
}

describe("Intraday Model Tests", () => {
  describe("handleCleanupData", () => {
    const initialState = {
      data: {
        [defaultVars.channel]: {
          0: {
            resource1: {},
          },
          1: {
            resource1: {},
            resource2: {},
          },
        },
      },
    }

    it("Should remove the resource to cleanup but keep the index when other resource exists", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 1, resource: "resource2" })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            0: {
              resource1: {},
            },
            1: {
              resource1: {},
            },
          },
        },
      })
    })

    it("Should remove the resource to cleanup and remove the index when no resources are left", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 0, resource: "resource1" })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            1: {
              resource1: {},
              resource2: {},
            },
          },
        },
      })
    })

    it("Should remove the index when no resource is specified", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 1 })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            0: {
              resource1: {},
            },
          },
        },
      })
    })

    it("Should not change the state when index does not exist", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 2 })
      expect(updatedState).toEqual(initialState)
    })

    it("Should not change the state when no index is passed", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel })
      expect(updatedState).toEqual(initialState)
    })
  })

  describe("handleQueryExecuted", () => {
    const initialState = {
      data: {
        [defaultVars.channel]: {
          0: {
            okResource: {
              some: "data",
              status: OK,
            },
            cancelledResource: {
              some: "data",
              status: CANCELLED,
            },
          },
        },
      },
    }

    const executeReducer = (resource, data) => handleQueryExecuted(initialState, { channel: defaultVars.channel, idx: 0, resource, data })

    it("Should update the data of the executed query resource", () => {
      const resource = "okResource"
      const updatedData = executeReducer(resource, { new: "data", status: OK })
      expect(get(updatedData, ["data", defaultVars.channel, "0", resource])).toEqual({ new: "data", status: OK })
    })

    it("Should not update the data of the executed query resource when it was cancelled", () => {
      const resource = "cancelledResource"
      const updatedData = executeReducer(resource, { new: "data", status: OK })
      expect(get(updatedData, ["data", defaultVars.channel, "0", resource])).toEqual({ some: "data", status: CANCELLED })
    })

    it("Should create the channel, index and resource when it does not already exist", () => {
      const updatedData = handleQueryExecuted(initialState, { channel: "newChannel", idx: 0, resource: "newResource", data: { some: "data", status: OK } })
      expect(updatedData).toEqual({
        data: {
          ...initialState.data,
          newChannel: {
            0: {
              newResource: {
                some: "data",
                status: OK,
              },
            },
          },
        },
      })
    })

    it("Should create the channel, index and resource when it does not already exist even if it was cancelled", () => {
      const updatedData = handleQueryExecuted(initialState, { channel: "newChannel", idx: 0, resource: "newResource", data: { some: "data", status: CANCELLED } })
      expect(updatedData).toEqual({
        data: {
          ...initialState.data,
          newChannel: {
            0: {
              newResource: {
                some: "data",
                status: CANCELLED,
              },
            },
          },
        },
      })
    })
  })

  describe("handleRegisterQuery", () => {
    const initialState = {
      queries: {
        [defaultVars.channel]: {
          [defaultVars.resource]: {
            modules: [defaultVars.module],
            query: "info",
          },
        },
      },
    }

    const executeReducer = (module, channel, resource, queryInfo) => handleRegisterQuery(initialState, { module, channel, resource, queryInfo })

    it("Registers a new query when it does not exist", () => {
      const updatedState = executeReducer(defaultVars.module, defaultVars.channel, "newResource", { query: "info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            ...initialState.queries[defaultVars.channel],
            newResource: {
              modules: [defaultVars.module],
              query: "info",
            },
          },
        },
      })
    })

    it("Updates a query's information when it exists for a module", () => {
      const updatedState = executeReducer(defaultVars.module, defaultVars.channel, defaultVars.resource, { query: "new-info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            [defaultVars.resource]: {
              modules: [defaultVars.module],
              query: "new-info",
            },
          },
        },
      })
    })

    it("Registers a query for a new module, and updates it", () => {
      const updatedState = executeReducer("newModule", defaultVars.channel, defaultVars.resource, { query: "new-info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            [defaultVars.resource]: {
              modules: [defaultVars.module, "newModule"],
              query: "new-info",
            },
          },
        },
      })
    })

    it("Registers a query for a new channel", () => {
      const updatedState = executeReducer(defaultVars.module, "newChannel", defaultVars.resource, { query: "info" })
      expect(updatedState).toEqual({
        queries: {
          ...initialState.queries,
          newChannel: {
            [defaultVars.resource]: {
              modules: [defaultVars.module],
              query: "info",
            },
          },
        },
      })
    })
  })

  describe("handleSetRefreshStatus", () => {
    it("Updates the refresh status correctly", () => {
      const updatedState = handleSetRefreshStatus(INITIAL_STATE, { status: REFRESHING, channel: defaultVars.channel, refreshIdx: defaultVars.refreshIdx, currentDataIdx: null })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        refresh: {
          status: REFRESHING,
          channel: defaultVars.channel,
          refreshIdx: defaultVars.refreshIdx,
          currentDataIdx: null,
        },
      })
    })
  })

  describe("handleUnregisterQuery", () => {
    const initialState = {
      ...INITIAL_STATE,
      refresh: {
        status: OK,
        currentDataIdx: defaultVars.currentDataIdx,
      },
      data: {
        channel1: {
          0: {
            resource3: {
              data: {},
            },
          },
        },
      },
      queries: {
        channel1: {
          resource1: {
            modules: ["module1"],
            query: "info",
          },
          resource2: {
            modules: ["module1", "module2"],
            query: "info",
          },
          resource3: {
            modules: ["module1"],
            query: "info",
          },
        },
        channel2: {
          [defaultVars.resource]: {
            modules: [defaultVars.module],
            query: "info",
          },
        },
      },
    }

    const executeReducer = (module, channel, resource) => handleUnregisterQuery(initialState, { module, channel, resource })

    it("Removes the query for a module, but keeps the resource if there is another module registered to it", () => {
      const updatedState = executeReducer("module1", "channel1", "resource2")
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...initialState.queries,
          channel1: {
            ...initialState.queries.channel1,
            resource2: {
              modules: ["module2"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query for a module, and removes the resource if it was the only module registered to it", () => {
      const updatedState = executeReducer("module1", "channel1", "resource1")
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...initialState.queries,
          channel1: {
            resource2: {
              modules: ["module1", "module2"],
              query: "info",
            },
            resource3: {
              modules: ["module1"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query for a module, removes the resource if it was the only module registered to it, and cleans up the data for that resource", () => {
      const updatedState = executeReducer("module1", "channel1", "resource3")
      expect(updatedState).toEqual({
        ...initialState,
        data: {
          channel1: {},
        },
        queries: {
          ...initialState.queries,
          channel1: {
            resource1: {
              modules: ["module1"],
              query: "info",
            },
            resource2: {
              modules: ["module1", "module2"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query and resource, and removes the channel if there is no query left on it", () => {
      const updatedState = executeReducer(defaultVars.module, "channel2", defaultVars.resource)
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...pick(initialState.queries, ["channel1"]),
        },
      })
    })
  })

  describe("handleRefreshQuery", () => {
    it("Sets the status to LOADING for a refreshing query", () => {
      const updatedState = handleRefreshQuery(INITIAL_STATE, { channel: defaultVars.channel, idx: 0, resource: defaultVars.resource })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        data: {
          [defaultVars.channel]: {
            0: {
              [defaultVars.resource]: {
                status: LOADING,
              },
            },
          },
        },
      })
    })
  })

  describe("handleCancelQuery", () => {
    it("Sets the status to CANCELLED for a cancelled query", () => {
      const updatedState = handleCancelQuery(INITIAL_STATE, { channel: defaultVars.channel, idx: 0, resource: defaultVars.resource })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        data: {
          [defaultVars.channel]: {
            0: {
              [defaultVars.resource]: {
                status: CANCELLED,
              },
            },
          },
        },
      })
    })
  })

  describe("handleQueueRefresh", () => {
    it("Records the channel whose announcement could not be served yet", () => {
      const updatedState = handleQueueRefresh(INITIAL_STATE, { channel: defaultVars.channel })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        pendingRefresh: { [defaultVars.channel]: true },
      })
    })

    it("Coalesces repeat announcements for one channel into a single pending entry", () => {
      const once = handleQueueRefresh(INITIAL_STATE, { channel: defaultVars.channel })
      const twice = handleQueueRefresh(once, { channel: defaultVars.channel })
      expect(twice.pendingRefresh).toEqual({ [defaultVars.channel]: true })
    })

    it("Keeps one entry per channel", () => {
      const first = handleQueueRefresh(INITIAL_STATE, { channel: defaultVars.channel })
      const second = handleQueueRefresh(first, { channel: "account_other" })
      expect(second.pendingRefresh).toEqual({ [defaultVars.channel]: true, account_other: true })
    })
  })

  describe("handleClearQueuedRefresh", () => {
    it("Removes only the channel being replayed", () => {
      const pending = handleQueueRefresh(handleQueueRefresh(INITIAL_STATE, { channel: defaultVars.channel }), { channel: "account_other" })
      const updatedState = handleClearQueuedRefresh(pending, { channel: defaultVars.channel })
      expect(updatedState.pendingRefresh).toEqual({ account_other: true })
    })

    it("Is a no-op for a channel that was never queued", () => {
      const updatedState = handleClearQueuedRefresh(INITIAL_STATE, { channel: defaultVars.channel })
      expect(updatedState.pendingRefresh).toEqual({})
    })
  })

  describe("handleRegisterQuerySaga", () => {
    const refreshingSelectorsReturn = [
      {
        status: REFRESHING,
        refreshIdx: defaultVars.refreshIdx,
      },
      {
        [defaultVars.channel]: {
          [defaultVars.resource]: {},
        },
      },
    ]

    beforeEach(() => {
      // Cleanup the sockets to make sure it is empty before each test
      closeAllSockets()
    })

    it("Cancels the query if a refresh is currently running", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next(refreshingSelectorsReturn)
        .put(Creators.cancelQuery(channel, 0, resource))
        .next()
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Registers the graphQL query and runs it", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Registers the api query and runs it", () => {
      const { channel, resource, queryInfo: { id, url, payload, noToken } } = apiAction
      testSaga(handleRegisterQuerySaga, apiAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(DataCreators.load(resource, id, url, payload, noToken))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Re-arms the listener even when a socket already exists, because the listener is what a re-register restores", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      openSocket(channel)
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })
  })

  describe("handleUnregisterQuerySaga", () => {
    it("Cancels the query if a refresh is currently running", () => {
      testSaga(handleUnregisterQuerySaga, { resource: defaultVars.resource })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ refresh: { status: REFRESHING, channel: defaultVars.channel, refreshIdx: defaultVars.refreshIdx }, queries: { [defaultVars.channel]: { [defaultVars.resource]: {} } } })
        .put(Creators.cancelQuery(defaultVars.channel, defaultVars.refreshIdx, defaultVars.resource))
        .next()
        .isDone()
    })

    it("Sets the resource data to all nulls if it is no longer registered", () => {
      testSaga(handleUnregisterQuerySaga, { resource: defaultVars.resource })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ refresh: { status: OK }, queries: {} })
        .put(GraphqlCreators.loadSuccess(defaultVars.resource, null, null, null, null))
        .next()
        .isDone()
    })
  })

  describe("handleUnregisterModuleQueries", () => {
    it("Unregisters queries for the selected module, if it is the only module still using it", () => {
      const module = "module1"
      const queries = {
        [defaultVars.channel]: {
          resource1: {
            modules: [module],
          },
          resource2: {
            modules: [module],
          },
          resource3: {
            modules: [module, "module2"],
          },
          // Should keep since module1 is not using it
          resource4: {
            modules: ["module2"],
          },
        },
      }
      testSaga(handleUnregisterModuleQueries, { module })
        .next()
        .select(sagaSelectors.getQueries)
        .next(queries)
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource1"))
        .next()
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource2"))
        .next()
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource3"))
        .next()
        .isDone()
    })
  })

  describe("handleRefreshData", () => {
    const queries = {
      [defaultVars.channel]: {
        resource1: {
          query: "info",
        },
        resource2: {
          query: "info",
        },
      },
    }

    it("Should not do anything if there are no queries to refresh", () => {
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: 0, status: OK },
          { channel: {} },
        ])
        .isDone()
    })

    it("Should queue the refresh instead of dropping it if a refresh is ongoing", () => {
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: defaultVars.currentDataIdx, status: REFRESHING },
          queries,
        ])
        .put(Creators.queueRefresh(defaultVars.channel))
        .next()
        .isDone()
    })

    it("Should change the status to refreshing and trigger the refresh for all queries of the channel", () => {
      const refreshIdx = defaultVars.currentDataIdx + 1
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: defaultVars.currentDataIdx, status: OK },
          queries,
        ])
        .put(Creators.setRefreshStatus(REFRESHING, defaultVars.channel, refreshIdx, defaultVars.currentDataIdx))
        .next()
        .put(Creators.refreshQuery(defaultVars.channel, refreshIdx, "resource1", queries[defaultVars.channel].resource1))
        .next()
        .put(Creators.refreshQuery(defaultVars.channel, refreshIdx, "resource2", queries[defaultVars.channel].resource2))
        .next()
        .isDone()
    })
  })

  describe("handleRefreshQuerySaga", () => {
    const defaultParams = {
      channel: defaultVars.channel,
      resource: defaultVars.resource,
      idx: defaultVars.refreshIdx,
    }

    const defaultIntradaySelectorResult = {
      data: {
        [defaultParams.channel]: {
          [defaultParams.idx]: {
            [defaultParams.resource]: {
              status: OK,
            },
          },
        },
      },
      refresh: {
        status: REFRESHING,
        channel: defaultParams.channel,
        refreshIdx: defaultParams.idx,
      },
    }

    it("Executes a graphql query, but nothing more if not all queries are completed", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next({
          ...defaultIntradaySelectorResult,
          data: {
            [defaultParams.channel]: {
              [defaultParams.idx]: {
                ...get(defaultIntradaySelectorResult, [defaultParams.channel, defaultParams.idx]),
                resource1: {
                  status: LOADING,
                },
              },
            },
          },
        })
        .isDone()
    })

    it("Executes a graphql query, and calls allQueriesExecuted action when all queries are done", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a POST API query", () => {
      const { queryInfo } = apiAction
      const { id, url, payload, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(apiPost, url, payload, noToken)
        .next("result")
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a GET API query", () => {
      const { queryInfo } = apiAction
      const { id, url, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo: { ...queryInfo, payload: null } })
        .next()
        .call(apiGet, url, noToken)
        .next("result")
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a POST API query and handles an error", () => {
      const { queryInfo } = apiAction
      const { id, url, payload, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(apiPost, url, payload, noToken)
        .throw("Error")
        .put(DataCreators.loadError(defaultParams.resource, id, "Error"))
        .next()
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: ERROR }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Does not settle a cycle the live refresh has already moved past", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ ...defaultIntradaySelectorResult, refresh: { status: REFRESHING, channel: defaultParams.channel, refreshIdx: defaultParams.idx + 1 } })
        .isDone()
    })

    it("Does not settle against a refresh running on another channel", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ ...defaultIntradaySelectorResult, refresh: { status: REFRESHING, channel: "account_otherResource", refreshIdx: defaultParams.idx } })
        .isDone()
    })
  })

  describe("handleAllQueriesExecuted", () => {
    const dataSomeCancelled = {
      [defaultVars.channel]: {
        [defaultVars.refreshIdx]: {
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource2: {
            status: CANCELLED,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        },
      },
    }

    const dataAllCancelled = {
      [defaultVars.channel]: {
        [defaultVars.refreshIdx]: {
          [defaultVars.resource]: {
            data: "data resource",
            status: CANCELLED,
          },
        },
      },
    }

    const queries = {
      [defaultVars.channel]: {
        resource1: {
          keepDataHistory: true,
        },
        resource2: {
          keepDataHistory: false,
        },
        resource3: {
          keepDataHistory: false,
        },
      },
    }

    it("Transfers the data of all successfully executed queries to global.data, with some CANCELLED queries", () => {
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: defaultVars.refreshIdx })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data: dataSomeCancelled, queries, refresh: { currentDataIdx: null } })
        .put(DataCreators.intradayLoadSuccess({
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        }))
        .next()
        .put(Creators.setRefreshStatus(OK, null, null, defaultVars.refreshIdx))
        .next()
    })

    it("Does not transfer the data with all queries cancelled", () => {
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: defaultVars.refreshIdx })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data: dataAllCancelled, queries, refresh: { currentDataIdx: null } })
        .put(Creators.setRefreshStatus(OK, null, null, defaultVars.refreshIdx))
        .next()
    })

    it("Transfers the data of all successfully executed queries to global.data and cleans up the data that is not marked keepDataHistory", () => {
      const data = {
        [defaultVars.channel]: {
          [defaultVars.refreshIdx]: {
            ...get(dataSomeCancelled, [defaultVars.channel, defaultVars.refreshIdx]),
          },
          1: {
            ...get(dataSomeCancelled, [defaultVars.channel, defaultVars.refreshIdx]),
          },
        },
      }
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: 1 })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data, queries, refresh: { currentDataIdx: defaultVars.currentDataIdx } })
        .put(DataCreators.intradayLoadSuccess({
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        }))
        .next()
        .put(Creators.cleanupData(defaultVars.channel, defaultVars.currentDataIdx, "resource2"))
        .next()
        .put(Creators.cleanupData(defaultVars.channel, defaultVars.currentDataIdx, "resource3"))
        .next()
        .put(Creators.setRefreshStatus(OK, null, null, 1))
        .next()
    })
  })
})

const asBatch = (id, at) => ({ id, date: at.format("YYYY-MM-DD"), asOf: at.format("HH:mm:ssZ") })

const requestedAsOf = (url) => {
  const match = /[?&]asOf=([^&]+)/.exec(url)
  return match ? moment(decodeURIComponent(match[1])) : null
}

const lastCompletedFrom = timeline => (url) => {
  const asOf = requestedAsOf(url) || moment()
  const completed = filter(timeline, entry => entry.at.isSameOrBefore(asOf))
  return isEmpty(completed) ? {} : last(completed).batch
}

const storeRootReducer = (state, action) => ({
  ...state,
  global: {
    ...state.global,
    intraday: intradayReducer(state.global.intraday, action),
    data: dataReducer(state.global.data, action),
  },
})

const mimicChannelReRegistration = (store, { accountAlias, portfolioId, channel, resource }) => {
  let seenIdx = get(store.getState(), "global.intraday.refresh.currentDataIdx")
  store.subscribe(() => {
    const idx = get(store.getState(), "global.intraday.refresh.currentDataIdx")
    if (idx === seenIdx) return
    seenIdx = idx
    const url = generateIntradayBatchUrlAt(accountAlias, portfolioId, moment().format())
    store.dispatch(Creators.registerQuery("view", channel, resource, { url, id: url }))
  })
}

const DISPATCH_CAP = 300

const createDispatchRecorder = () => {
  const actions = []
  return {
    actions,
    middleware: () => next => (action) => {
      actions.push(action)
      return actions.length > DISPATCH_CAP ? action : next(action)
    },
  }
}

const settle = async () => {
  for (let turn = 0; turn < 12; turn += 1) {
    await new Promise(resolve => setTimeout(resolve, 0))
  }
}

// `settle` awaits real setTimeouts, which never fire while the clock is faked — calling it under
// fake timers hangs rather than fails. This drains the same turns and moves the clock, so a pending
// `delay` in the saga actually elapses.
const settleFakeTimers = async (ms = 0) => {
  for (let turn = 0; turn < 12; turn += 1) {
    await jest.advanceTimersByTimeAsync(turn === 0 ? ms : 0)
  }
}

describe("Intraday reconcile on socket connect", () => {
  const accountAlias = "acme"
  const accountId = "11111111-1111-1111-1111-111111111111"
  const portfolioId = "22222222-2222-2222-2222-222222222222"
  const channel = `${accountId}_${portfolioId}`
  const dashboardResource = intradayLastBatchResource(portfolioId)
  const legacyResource = INTRADAY_LAST_BATCH_RESOURCE

  const olderAt = moment().subtract(3, "minutes")
  const newerAt = moment().subtract(1, "minutes")
  const olderBatch = asBatch("batch-2", olderAt)
  const newerBatch = asBatch("batch-3", newerAt)
  const serverTimeline = [{ at: olderAt, batch: olderBatch }, { at: newerAt, batch: newerBatch }]
  const frozenAsOf = moment().subtract(2, "minutes")

  const lastCompletedFor = lastCompletedFrom(serverTimeline)
  const respondFromFakeServer = url => Promise.resolve().then(() => lastCompletedFor(url))

  const preloadedState = ({ resource, displayed, registered, registeredAsOf, registeredUrl }) => {
    const url = registeredUrl || generateIntradayBatchUrlAt(accountAlias, portfolioId, registeredAsOf.format())
    return {
      global: {
        selected: { account: accountAlias, accountId },
        intraday: {
          ...INITIAL_STATE,
          queries: registered ? { [channel]: { [resource]: { modules: ["view"], url, id: url } } } : {},
        },
        data: { [resource]: { status: OK, id: url, data: displayed } },
      },
    }
  }

  const startChannel = ({ resource = dashboardResource, displayed = olderBatch, registered = true, registeredAsOf = frozenAsOf, registeredUrl = null, alreadyConnected = false } = {}) => {
    const sagaMiddleware = createSagaMiddleware()
    const store = createStore(storeRootReducer, preloadedState({ resource, displayed, registered, registeredAsOf, registeredUrl }), applyMiddleware(createDispatchRecorder().middleware, sagaMiddleware))
    sagaMiddleware.run(saga)
    sagaMiddleware.run(dataSaga)
    openSocket(channel)
    // The drill-down case: the socket outlived the teardown grace, so it is already connected when
    // the listener arms and no handler is attached to hear the `connect` it emitted earlier.
    if (alreadyConnected) {
      mockEmitSocketEvent("connect")
    }
    store.dispatch(Creators.initIntradayListener(channel))
    mimicChannelReRegistration(store, { accountAlias, portfolioId, channel, resource })
    return store
  }

  const displayedBatchIn = (store, resource) => get(store.getState(), ["global", "data", resource, "data"])
  const refreshIdxIn = store => get(store.getState(), "global.intraday.refresh.currentDataIdx")

  beforeEach(() => {
    closeAllSockets()
    mockResetSocketListeners()
    apiGet.mockReset()
    apiGet.mockImplementation(respondFromFakeServer)
  })

  it("Adopts a batch that committed while the socket was down, once it reconnects", async () => {
    const store = startChannel()

    mockEmitSocketEvent("connect")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Adopts the new batch on the legacy analyze/rebalance resource key", async () => {
    const store = startChannel({ resource: legacyResource })

    mockEmitSocketEvent("connect")
    await settle()

    expect(displayedBatchIn(store, legacyResource)).toEqual(newerBatch)
  })

  it("Probes with a fresh asOf rather than the registered query's frozen one", async () => {
    startChannel()

    mockEmitSocketEvent("connect")
    await settle()

    const probeUrl = apiGet.mock.calls[0][0]
    expect(probeUrl).toContain(`/p/${portfolioId}/intraday/batches/last-completed`)
    expect(requestedAsOf(probeUrl).isAfter(frozenAsOf)).toBe(true)
  })

  it("Reconciles after a drill-down teardown nulled the displayed batch", async () => {
    const store = startChannel({ displayed: null })

    mockEmitSocketEvent("connect")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Adopts the day's first batch when the displayed payload is the server's empty response", async () => {
    const store = startChannel({ displayed: {} })

    mockEmitSocketEvent("connect")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Adopts rather than stalls when there is no baseline to compare against at all", async () => {
    const store = startChannel({
      displayed: null,
      registeredUrl: `/api/${accountAlias}/p/${portfolioId}/intraday/batches/last-completed`,
    })

    mockEmitSocketEvent("connect")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Does not refresh when the server has no completed batch yet, even with no baseline to compare against", async () => {
    // Reconnecting before the day's first batch commits: the server answers {}, and there is no
    // floor either, so only the no-batch guard stands between this and a pointless channel refresh.
    apiGet.mockImplementation(() => Promise.resolve({}))
    const store = startChannel({
      displayed: null,
      registeredUrl: `/api/${accountAlias}/p/${portfolioId}/intraday/batches/last-completed`,
    })

    mockEmitSocketEvent("connect")
    await settle()

    expect(apiGet).toHaveBeenCalledTimes(1)
    expect(refreshIdxIn(store)).toBeNull()
  })

  it("Does not refresh on a page load's first connect, when the pending query can still return the current batch", async () => {
    const store = startChannel({ displayed: null, registeredAsOf: moment() })

    mockEmitSocketEvent("connect")
    await settle()

    expect(apiGet).toHaveBeenCalledTimes(1)
    expect(refreshIdxIn(store)).toBeNull()
  })

  it("Does not probe when the channel has no last-batch query registered", async () => {
    startChannel({ registered: false })

    mockEmitSocketEvent("connect")
    await settle()

    expect(apiGet).not.toHaveBeenCalled()
  })

  it("Leaves the displayed batch alone when the reconnect finds nothing newer", async () => {
    const store = startChannel({ displayed: newerBatch })

    mockEmitSocketEvent("connect")
    await settle()

    expect(apiGet).toHaveBeenCalledTimes(1)
    expect(refreshIdxIn(store)).toBeNull()
    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Does not drop a batch announcement that arrives while the reconcile probe is in flight", async () => {
    const store = startChannel({ displayed: newerBatch })
    let announced = false
    apiGet.mockImplementation((url) => {
      if (!announced) {
        announced = true
        mockEmitSocketEvent("intraday")
      }
      return respondFromFakeServer(url)
    })

    mockEmitSocketEvent("connect")
    await settle()

    expect(refreshIdxIn(store)).toBe(0)
  })

  it("Logs a failed probe at error level, naming the channel and resource, and keeps listening", async () => {
    const store = startChannel()
    const logged = jest.spyOn(console, "error").mockImplementation(() => {})
    apiGet.mockImplementationOnce(() => Promise.reject(new Error("network down")))

    mockEmitSocketEvent("connect")
    await settle()

    expect(logged).toHaveBeenCalledTimes(1)
    const [message, reported] = logged.mock.calls[0]
    expect(message).toContain(channel)
    expect(message).toContain(dashboardResource)
    expect(reported).toEqual(new Error("network down"))
    expect(refreshIdxIn(store)).toBeNull()

    mockEmitSocketEvent("intraday")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
    logged.mockRestore()
  })

  it("Adopts a batch that committed while a kept-alive socket stayed connected, once the listener re-arms", async () => {
    const store = startChannel({ alreadyConnected: true })
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  it("Leaves the reconcile to the connect event when the listener arms before the handshake completes", async () => {
    const store = startChannel({ displayed: newerBatch })
    await settle()

    expect(apiGet).not.toHaveBeenCalled()

    mockEmitSocketEvent("connect")
    await settle()

    expect(apiGet).toHaveBeenCalledTimes(1)
    expect(refreshIdxIn(store)).toBeNull()
  })

  it("Still refreshes when the batch announcement is delivered", async () => {
    const store = startChannel()

    mockEmitSocketEvent("intraday")
    await settle()

    expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
  })

  describe("when the probe never answers", () => {
    // Wall-clock, not RECONCILE_PROBE_TIMEOUT_MS: a window read off the constant under test cannot
    // tell a five-second deadline from a five-thousand-second one.
    const deadlineMs = 5000

    beforeEach(() => {
      jest.useFakeTimers()
    })

    afterEach(() => {
      jest.useRealTimers()
    })

    it("Gives up five seconds in, keeps the displayed batch, and still serves the next announcement", async () => {
      const store = startChannel()
      const logged = jest.spyOn(console, "error").mockImplementation(() => {})
      apiGet.mockImplementationOnce(() => new Promise(() => {}))

      mockEmitSocketEvent("connect")
      await settleFakeTimers(deadlineMs - 1)

      expect(logged).not.toHaveBeenCalled()

      await settleFakeTimers(2)

      expect(logged).toHaveBeenCalledTimes(1)
      const [message] = logged.mock.calls[0]
      expect(message).toContain(channel)
      expect(message).toContain(dashboardResource)
      expect(displayedBatchIn(store, dashboardResource)).toEqual(olderBatch)
      expect(refreshIdxIn(store)).toBeNull()

      mockEmitSocketEvent("intraday")
      await settleFakeTimers()

      expect(displayedBatchIn(store, dashboardResource)).toEqual(newerBatch)
      logged.mockRestore()
    })
  })
})

describe("Intraday refresh arriving mid-refresh", () => {
  const accountAlias = "acme"
  const accountId = "11111111-1111-1111-1111-111111111111"
  const portfolioA = "33333333-3333-3333-3333-333333333333"
  const portfolioB = "44444444-4444-4444-4444-444444444444"
  const portfolioC = "55555555-5555-5555-5555-555555555555"
  const channelA = `${accountId}_${portfolioA}`
  const channelB = `${accountId}_${portfolioB}`
  const channelC = `${accountId}_${portfolioC}`

  const DEFAULT_ANALYTICS_COUNTS = { [portfolioA]: 4, [portfolioB]: 2, [portfolioC]: 2 }
  let analyticsCounts
  const analyticsOn = portfolio => Array.from({ length: analyticsCounts[portfolio] }, (unused, index) => `analytics${index + 1}-${portfolio}`)
  const analyticsUrlFor = resource => `/api/${accountAlias}/analytics/${resource}?intraday=true`

  const olderAt = moment().subtract(3, "minutes")
  const newerAt = moment().subtract(1, "minutes")
  const olderBatch = asBatch("batch-2", olderAt)
  const newerBatch = asBatch("batch-3", newerAt)
  const frozenAsOf = moment().subtract(2, "minutes")

  let serverTimeline
  let lastCompletedFor
  let heldFetches
  let dispatched

  const commitNewerBatch = () => serverTimeline.push({ at: newerAt, batch: newerBatch })

  const respondFromFakeServer = url => Promise.resolve().then(() => lastCompletedFor(url))

  const respondHoldingFetches = (url) => {
    const body = lastCompletedFor(url)
    if (!heldFetches) return Promise.resolve().then(() => body)
    return new Promise(resolve => heldFetches.push(() => resolve(body)))
  }

  const releaseHeldFetches = () => {
    const held = heldFetches
    heldFetches = null
    forEach(held, release => release())
  }

  const queriesFor = (portfolio) => {
    const channel = `${accountId}_${portfolio}`
    const batchResource = intradayLastBatchResource(portfolio)
    const batchUrl = generateIntradayBatchUrlAt(accountAlias, portfolio, frozenAsOf.format())
    const queries = { [batchResource]: { modules: ["view"], url: batchUrl, id: batchUrl } }
    forEach(analyticsOn(portfolio), (resource) => {
      queries[resource] = { modules: ["view"], url: analyticsUrlFor(resource), id: analyticsUrlFor(resource) }
    })
    return { channel, batchResource, batchUrl, queries }
  }

  const preloadedState = (portfolios) => {
    const queries = {}
    const data = {}
    forEach(portfolios, (portfolio) => {
      const channelQueries = queriesFor(portfolio)
      queries[channelQueries.channel] = channelQueries.queries
      data[channelQueries.batchResource] = { status: OK, id: channelQueries.batchUrl, data: olderBatch }
    })
    return {
      global: {
        selected: { account: accountAlias, accountId },
        intraday: { ...INITIAL_STATE, queries },
        data,
      },
    }
  }

  const startChannels = (portfolios = [portfolioA], { reRegister = true, rootReducer = storeRootReducer } = {}) => {
    const sagaMiddleware = createSagaMiddleware()
    const recorder = createDispatchRecorder()
    dispatched = recorder.actions
    const store = createStore(rootReducer, preloadedState(portfolios), applyMiddleware(recorder.middleware, sagaMiddleware))
    sagaMiddleware.run(saga)
    sagaMiddleware.run(dataSaga)
    forEach(portfolios, (portfolio) => {
      const channel = `${accountId}_${portfolio}`
      openSocket(channel)
      store.dispatch(Creators.initIntradayListener(channel))
      if (reRegister) {
        mimicChannelReRegistration(store, { accountAlias, portfolioId: portfolio, channel, resource: intradayLastBatchResource(portfolio) })
      }
    })
    return store
  }

  const dataIn = (store, resource) => get(store.getState(), ["global", "data", resource, "data"])
  const refreshStatusIn = store => get(store.getState(), "global.intraday.refresh.status")
  const pendingIn = store => get(store.getState(), "global.intraday.pendingRefresh")

  const refreshedResources = (channel, idx) => dispatched
    .filter(action => action.type === Types.REFRESH_QUERY && action.channel === channel && action.idx === idx)
    .map(action => action.resource)
    .sort()

  const refreshedIndexes = channel => uniq(dispatched
    .filter(action => action.type === Types.REFRESH_QUERY && action.channel === channel)
    .map(action => action.idx))

  const fetchCountFor = resource => apiGet.mock.calls.filter(([url]) => url === analyticsUrlFor(resource)).length

  beforeEach(() => {
    closeAllSockets()
    mockResetSocketListeners()
    apiGet.mockReset()
    analyticsCounts = { ...DEFAULT_ANALYTICS_COUNTS }
    serverTimeline = [{ at: olderAt, batch: olderBatch }]
    lastCompletedFor = lastCompletedFrom(serverTimeline)
    heldFetches = null
    dispatched = []
  })

  it("Replays an announcement that arrived mid-refresh, onto the batch the server has by then", async () => {
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels()
    const [firstAnalytics] = analyticsOn(portfolioA)

    mockEmitSocketEvent("intraday")
    await settle()
    expect(fetchCountFor(firstAnalytics)).toBe(1)

    commitNewerBatch()
    mockEmitSocketEvent("intraday")
    await settle()
    expect(pendingIn(store)).toEqual({ [channelA]: true })

    releaseHeldFetches()
    await settle()

    expect(fetchCountFor(firstAnalytics)).toBe(2)
    expect(dataIn(store, firstAnalytics)).toEqual(newerBatch)
    expect(refreshStatusIn(store)).not.toEqual(REFRESHING)
    expect(pendingIn(store)).toEqual({})
  })

  it("Replays every query on the channel, not just the ones dispatched before the next replay", async () => {
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels([portfolioA, portfolioB])

    store.dispatch(Creators.refreshData(channelA))
    await settle()
    const inFlightIdx = get(store.getState(), "global.intraday.refresh.refreshIdx")

    store.dispatch(Creators.refreshData(channelA))
    store.dispatch(Creators.refreshData(channelB))
    await settle()
    expect(pendingIn(store)).toEqual({ [channelA]: true, [channelB]: true })

    releaseHeldFetches()
    await settle()

    const replayIdx = inFlightIdx + 1
    expect(refreshedResources(channelA, replayIdx)).toEqual([...analyticsOn(portfolioA), intradayLastBatchResource(portfolioA)].sort())
    expect(refreshedIndexes(channelB)).not.toHaveLength(0)
    expect(pendingIn(store)).toEqual({})
    expect(dispatched.length).toBeLessThan(DISPATCH_CAP)
  })

  it("Coalesces several announcements during one cycle into a single replay", async () => {
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels()

    store.dispatch(Creators.refreshData(channelA))
    await settle()
    const inFlightIdx = get(store.getState(), "global.intraday.refresh.refreshIdx")

    store.dispatch(Creators.refreshData(channelA))
    store.dispatch(Creators.refreshData(channelA))
    store.dispatch(Creators.refreshData(channelA))
    await settle()

    releaseHeldFetches()
    await settle()

    expect(refreshedIndexes(channelA)).toEqual([inFlightIdx, inFlightIdx + 1])
    expect(dispatched.length).toBeLessThan(DISPATCH_CAP)
  })

  it("Does not queue a mid-cycle announcement for a channel with nothing registered on it", async () => {
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels()

    store.dispatch(Creators.refreshData(channelA))
    await settle()

    store.dispatch(Creators.refreshData("account_unregistered"))
    await settle()
    expect(pendingIn(store)).toEqual({})

    releaseHeldFetches()
    await settle()
    expect(refreshedIndexes("account_unregistered")).toHaveLength(0)
  })

  it("Serves a later pending channel even when the first one no longer has queries to refresh", async () => {
    analyticsCounts = { ...DEFAULT_ANALYTICS_COUNTS, [portfolioA]: 0 }
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels([portfolioA, portfolioB], { reRegister: false })

    store.dispatch(Creators.refreshData(channelA))
    await settle()

    store.dispatch(Creators.refreshData(channelA))
    store.dispatch(Creators.refreshData(channelB))
    await settle()
    expect(pendingIn(store)).toEqual({ [channelA]: true, [channelB]: true })

    store.dispatch(Creators.unregisterQuery("view", channelA, intradayLastBatchResource(portfolioA)))
    await settle()

    releaseHeldFetches()
    await settle()

    expect(dispatched.filter(action => action.type === Types.ALL_QUERIES_EXECUTED && action.channel === channelA)).toHaveLength(1)
    expect(refreshedResources(channelB, 1)).toEqual([...analyticsOn(portfolioB), intradayLastBatchResource(portfolioB)].sort())
    expect(pendingIn(store)).toEqual({})
    expect(dispatched.length).toBeLessThan(DISPATCH_CAP)
  })

  it("Does not replay a refresh nothing asked for", async () => {
    apiGet.mockImplementation(respondFromFakeServer)
    const store = startChannels()
    const [firstAnalytics] = analyticsOn(portfolioA)

    mockEmitSocketEvent("intraday")
    await settle()

    expect(fetchCountFor(firstAnalytics)).toBe(1)
    expect(refreshedIndexes(channelA)).toHaveLength(1)
    expect(refreshStatusIn(store)).not.toEqual(REFRESHING)
    expect(dataIn(store, firstAnalytics)).toEqual(olderBatch)
  })

  it("Settles the refresh at ERROR when a query fails, so later announcements are still honoured", async () => {
    apiGet.mockImplementation(() => Promise.reject(new Error("intraday analytics unavailable")))
    const store = startChannels()
    const [firstAnalytics] = analyticsOn(portfolioA)

    mockEmitSocketEvent("intraday")
    await settle()

    expect(refreshStatusIn(store)).toEqual(ERROR)
    expect(get(store.getState(), ["global", "data", firstAnalytics, "error", "message"])).toEqual("intraday analytics unavailable")

    commitNewerBatch()
    apiGet.mockImplementation(respondFromFakeServer)
    mockEmitSocketEvent("intraday")
    await settle()

    expect(dataIn(store, firstAnalytics)).toEqual(newerBatch)
  })

  it("Fires ALL_QUERIES_EXECUTED once when a cycle's queries all settle in the same tick", async () => {
    analyticsCounts = { ...DEFAULT_ANALYTICS_COUNTS, [portfolioA]: 1 }
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const store = startChannels([portfolioA], { reRegister: false })
    const [analytics] = analyticsOn(portfolioA)

    store.dispatch(Creators.refreshData(channelA))
    await settle()
    expect(heldFetches).toHaveLength(2)

    // A queued replay is waiting, so a second settle would drain again and refetch the whole channel.
    store.dispatch(Creators.refreshData(channelA))
    await settle()
    expect(pendingIn(store)).toEqual({ [channelA]: true })

    releaseHeldFetches()
    await settle()

    expect(dispatched.filter(action => action.type === Types.ALL_QUERIES_EXECUTED && action.channel === channelA && action.idx === 0)).toHaveLength(1)
    expect(refreshedIndexes(channelA)).toEqual([0, 1])
    expect(fetchCountFor(analytics)).toBe(2)
  })

  it("Does not let a settle from a superseded cycle clobber the live one", async () => {
    analyticsCounts = { ...DEFAULT_ANALYTICS_COUNTS, [portfolioA]: 2 }
    const [liveAnalytics, unmountedAnalytics] = analyticsOn(portfolioA)
    const { batchUrl } = queriesFor(portfolioA)
    const attempts = {}
    const held = {}
    apiGet.mockImplementation((url) => {
      attempts[url] = (attempts[url] || 0) + 1
      const body = { attempt: attempts[url] }
      return new Promise((resolve) => { held[url] = [...(held[url] || []), () => resolve(body)] })
    })
    const releaseNext = (url) => {
      const [next, ...rest] = held[url] || []
      held[url] = rest
      if (next) next()
    }
    const store = startChannels([portfolioA], { reRegister: false })

    store.dispatch(Creators.refreshData(channelA))
    await settle()

    store.dispatch(Creators.refreshData(channelA))
    await settle()
    expect(pendingIn(store)).toEqual({ [channelA]: true })

    // A module unmounting mid-refresh cancels its query, so the cycle settles with that fetch still outstanding.
    store.dispatch(Creators.unregisterQuery("view", channelA, unmountedAnalytics))
    await settle()

    releaseNext(analyticsUrlFor(liveAnalytics))
    releaseNext(batchUrl)
    await settle()
    expect(refreshedResources(channelA, 1)).toEqual([intradayLastBatchResource(portfolioA), liveAnalytics].sort())

    releaseNext(analyticsUrlFor(unmountedAnalytics))
    await settle()

    releaseNext(analyticsUrlFor(liveAnalytics))
    releaseNext(batchUrl)
    await settle()

    expect(dataIn(store, liveAnalytics)).toEqual({ attempt: 2 })
    expect(get(store.getState(), "global.intraday.refresh.currentDataIdx")).toBe(1)
  })

  it("Keeps the intraday saga alive when replaying a queued refresh throws, and drains the channels behind it", async () => {
    analyticsCounts = { ...DEFAULT_ANALYTICS_COUNTS, [portfolioA]: 1 }
    const rejectRefreshOn = channel => (state, action) => {
      if (action.type === Types.SET_REFRESH_STATUS && action.channel === channel) {
        throw new Error(`refresh state write rejected for ${channel}`)
      }
      return storeRootReducer(state, action)
    }
    heldFetches = []
    apiGet.mockImplementation(respondHoldingFetches)
    const logged = jest.spyOn(console, "error").mockImplementation(() => {})
    const store = startChannels([portfolioA, portfolioB, portfolioC], { reRegister: false, rootReducer: rejectRefreshOn(channelB) })

    store.dispatch(Creators.refreshData(channelA))
    await settle()

    store.dispatch(Creators.refreshData(channelB))
    store.dispatch(Creators.refreshData(channelC))
    await settle()
    expect(pendingIn(store)).toEqual({ [channelB]: true, [channelC]: true })

    releaseHeldFetches()
    await settle()

    expect(logged.mock.calls.filter(([message]) => `${message}`.includes("could not replay the queued refresh") && `${message}`.includes(channelB))).toHaveLength(1)
    expect(refreshedResources(channelC, 1)).toEqual([...analyticsOn(portfolioC), intradayLastBatchResource(portfolioC)].sort())
    expect(pendingIn(store)).toEqual({})

    store.dispatch(Creators.refreshData(channelA))
    await settle()

    expect(refreshedIndexes(channelA)).toEqual([0, 2])
    logged.mockRestore()
  })
})

describe("Intraday socket teardown across an unregister/register transition", () => {
  const accountAlias = "acme"
  const accountId = "11111111-1111-1111-1111-111111111111"
  const portfolioId = "66666666-6666-6666-6666-666666666666"
  const channel = `${accountId}_${portfolioId}`
  const otherChannel = `${accountId}_77777777-7777-7777-7777-777777777777`
  const resource = intradayLastBatchResource(portfolioId)
  const siblingResource = "portfolioFilters"

  const registeredUrl = generateIntradayBatchUrlAt(accountAlias, portfolioId, moment().subtract(1, "minutes").format())
  const registered = { url: registeredUrl, id: registeredUrl }
  const siblingUrl = `/api/${accountAlias}/analytics/${siblingResource}?intraday=true`
  const sibling = { url: siblingUrl, id: siblingUrl }

  const preloadedState = ({ withSibling = false } = {}) => ({
    global: {
      selected: { account: accountAlias, accountId },
      intraday: {
        ...INITIAL_STATE,
        queries: {
          [channel]: {
            [resource]: { modules: ["outgoing-module"], ...registered },
            ...(withSibling ? { [siblingResource]: { modules: ["sibling-module"], ...sibling } } : {}),
          },
        },
      },
      data: {},
    },
  })

  const settlePastTheGrace = () => settleFakeTimers(SOCKET_TEARDOWN_GRACE_MS + 1)

  const startStore = (preloaded) => {
    const sagaMiddleware = createSagaMiddleware()
    const store = createStore(storeRootReducer, preloaded, applyMiddleware(createDispatchRecorder().middleware, sagaMiddleware))
    store.intradaySagaTask = sagaMiddleware.run(saga)
    sagaMiddleware.run(dataSaga)
    return store
  }

  const startChannel = (options) => {
    const store = startStore(preloadedState(options))
    openSocket(channel)
    store.dispatch(Creators.initIntradayListener(channel))
    return store
  }

  const unregister = (store, module, unregistered = resource) => store.dispatch(Creators.unregisterQuery(module, channel, unregistered))
  const register = (store, module) => store.dispatch(Creators.registerQuery(module, channel, resource, registered))
  const refreshIdxIn = store => get(store.getState(), "global.intraday.refresh.currentDataIdx")

  beforeEach(() => {
    jest.useFakeTimers()
    closeAllSockets()
    mockResetSocketListeners()
    apiGet.mockReset()
    apiGet.mockImplementation(() => Promise.resolve({}))
  })

  afterEach(() => {
    jest.useRealTimers()
  })

  it("Keeps the connection when the incoming module registers after the outgoing one unregistered", async () => {
    const store = startChannel()
    expect(mockSockets).toHaveLength(1)

    unregister(store, "outgoing-module")
    await settleFakeTimers()
    register(store, "incoming-module")
    await settleFakeTimers()

    await settlePastTheGrace()

    expect(mockSockets).toHaveLength(1)
    expect(getSocket(channel)).toBeDefined()
    expect(mockSockets[0].disconnectCount).toBe(0)
  })

  it("Keeps the connection through a second drill-down armed inside the first one's grace", async () => {
    const store = startChannel()

    unregister(store, "outgoing-module")
    await settleFakeTimers(50)
    register(store, "first-incoming")
    await settleFakeTimers(900)

    unregister(store, "first-incoming")
    await settleFakeTimers(60)
    register(store, "second-incoming")
    await settleFakeTimers()

    expect(mockSockets).toHaveLength(1)
    expect(getSocket(channel)).toBeDefined()
    expect(mockSockets[0].disconnectCount).toBe(0)
  })

  it("Keeps the connection when only one of two resources on the channel unregisters", async () => {
    const store = startChannel({ withSibling: true })

    unregister(store, "sibling-module", siblingResource)
    await settlePastTheGrace()

    expect(mockSockets).toHaveLength(1)
    expect(getSocket(channel)).toBeDefined()
    expect(mockSockets[0].disconnectCount).toBe(0)
  })

  it("Still closes the connection when the channel is genuinely abandoned", async () => {
    const store = startChannel()
    mockSockets[0].emitToClient("connect")

    unregister(store, "outgoing-module")
    await settleFakeTimers()

    await settlePastTheGrace()

    expect(getSocket(channel)).toBeUndefined()
    expect(mockSockets).toHaveLength(1)
    expect(mockSockets[0].disconnectCount).toBe(1)
  })

  it("Disconnects a socket abandoned before its handshake completed", async () => {
    const store = startChannel()
    expect(mockSockets[0].connected).toBe(false)

    unregister(store, "outgoing-module")
    await settlePastTheGrace()

    expect(getSocket(channel)).toBeUndefined()
    expect(mockSockets[0].disconnectCount).toBe(1)
  })

  it("Closes the connection when the saga tree is torn down before the grace elapses", async () => {
    const store = startChannel()

    unregister(store, "outgoing-module")
    await settleFakeTimers()
    expect(getSocket(channel)).toBeDefined()

    store.intradaySagaTask.cancel()
    await settleFakeTimers()

    expect(getSocket(channel)).toBeUndefined()
    expect(mockSockets[0].disconnectCount).toBe(1)
  })

  it("Keeps hearing the server after a drill-down, even when another channel took the listener", async () => {
    const store = startChannel()

    openSocket(otherChannel)
    store.dispatch(Creators.initIntradayListener(otherChannel))
    await settleFakeTimers()

    unregister(store, "outgoing-module")
    await settleFakeTimers()
    register(store, "incoming-module")
    await settleFakeTimers()

    mockSockets[0].emitToClient("intraday")
    await settleFakeTimers()

    expect(refreshIdxIn(store)).toBe(0)
  })

  it("Releases the listener when the saga tree dies, so a replacement store can re-arm it", async () => {
    const first = startChannel()
    await settleFakeTimers()

    first.intradaySagaTask.cancel()
    await settleFakeTimers()

    const second = startStore(preloadedState())

    register(second, "returning-module")
    await settleFakeTimers()

    last(mockSockets).emitToClient("intraday")
    await settleFakeTimers()

    expect(refreshIdxIn(second)).toBe(0)
  })

  it("Re-arms the listener on a channel whose socket was genuinely torn down and then re-registered", async () => {
    const store = startChannel()

    unregister(store, "outgoing-module")
    await settlePastTheGrace()
    expect(getSocket(channel)).toBeUndefined()

    register(store, "returning-module")
    await settleFakeTimers()
    expect(mockSockets).toHaveLength(2)

    mockSockets[1].emitToClient("intraday")
    await settleFakeTimers()

    expect(refreshIdxIn(store)).toBe(0)
  })
})
